import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  authClient,
  signIn as betterAuthSignIn,
  signUp as betterAuthSignUp,
  signOut as betterAuthSignOut,
  useSession,
} from '../lib/auth';
import { setAuthErrorHandler } from '../lib/api';
import type { User as ApiUser } from '@grove/api-client';

type User = Pick<ApiUser, 'id' | 'email' | 'name' | 'image'>;

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  oauthSignIn: (provider: 'google' | 'github') => Promise<void>;
  handleAuthError: () => void;
}

function assertNonEmpty(value: string, fieldLabel: string) {
  if (!value) throw new Error(`${fieldLabel} is required`);
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const user = (session?.user as User | undefined) ?? null;
  const isAdmin = user?.email === 'jeff@grovereader.com';

  const signIn = useCallback(async (email: string, password: string) => {
    const cleanEmail = email.trim();
    assertNonEmpty(cleanEmail, 'Email');
    assertNonEmpty(password, 'Password');
    const result = await betterAuthSignIn.email({ email: cleanEmail, password, callbackURL: '/queue' });
    if (result?.error) throw new Error(result.error.message ?? 'Sign in failed');
  }, []);

  const signUp = useCallback(async (email: string, password: string, name: string) => {
    const cleanEmail = email.trim();
    const cleanName = name.trim();
    assertNonEmpty(cleanName, 'Name');
    assertNonEmpty(cleanEmail, 'Email');
    if (password.length < 8) throw new Error('Password must be at least 8 characters');
    const result = await betterAuthSignUp.email({ name: cleanName, email: cleanEmail, password, callbackURL: '/queue' });
    if (result?.error) throw new Error(result.error.message ?? 'Registration failed');
  }, []);

  const signOut = useCallback(async () => {
    await betterAuthSignOut();
  }, []);

  const oauthSignIn = useCallback(async (provider: 'google' | 'github') => {
    await betterAuthSignIn.social({ provider, callbackURL: '/queue' });
  }, []);

  // Called on 401 — clears the stored session so the next render lands on /login.
  // Guarded against burst 401s (parallel requests after session expiry) so we
  // only fire one sign-out per expiry event.
  const signingOutRef = useRef(false);
  const handleAuthError = useCallback(() => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    authClient
      .signOut()
      .catch(() => {})
      .finally(() => { signingOutRef.current = false; });
  }, []);

  useEffect(() => {
    setAuthErrorHandler(handleAuthError);
    return () => setAuthErrorHandler(null);
  }, [handleAuthError]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading: isPending,
        isAuthenticated: !!user,
        isAdmin,
        signIn,
        signUp,
        signOut,
        oauthSignIn,
        handleAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#09090F' }}>
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  // Carry the intended destination through sign-in. Public share links send
  // people to /queue?save=<url>; losing that param would drop the save they
  // came here to make.
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}
